import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Atom } from "react-loading-indicators";
import { TextField, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowLeft, faCircleRight, faPenToSquare, faX, faCheck, faXmark, faSpinner, faUpload, faBell, faComments, faHeadset, faUserPlus, faEnvelope, faDesktop, faPlus, faTrash, faTriangleExclamation, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import BotChatStep from '../components/BotChatStep.js';
import BackButton from '../components/BackButton.js';
import WarningModal from '../components/WarningModal.js';
import SlackLogo from '../components/SlackLogo.js';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../components/BrandIcons';
import { authFetch } from '../utils/api';
import './WixRegister.css';

const WXR_NOTIFY_CHANNELS = [
  { key: "in_app", icon: faDesktop,  color: "#5616ea", label: "In-app" },
  { key: "email",  icon: faEnvelope, color: "#5616ea", label: "Email"  },
];

const WXR_NOTIFY_EVENTS = [
  { key: "new_chat",       section: "live_chat", icon: faComments,  title: "New chat started",       desc: "A visitor just opened the chat for the first time." },
  { key: "help_requested", section: "live_chat", icon: faHeadset,   title: "Visitor wants a human",  desc: "Someone clicked \u201CTalk to a human\u201D \u2014 jump in to take over.", highlight: true },
  { key: "new_lead",       section: "leads",     icon: faUserPlus,  title: "New lead captured",      desc: "A visitor shared their contact details with your bot." },
  { key: "new_message",    section: "live_chat", icon: faEnvelope,  title: "New message in joined chat", desc: "A visitor replied in a chat you joined. In-app only to avoid spam." },
];

const WXR_LOCKED_CHANNELS = {
  new_message: ["email", "slack", "teams", "telegram"],
};
const wxrIsChannelLocked = (eventKey, channelKey) =>
  (WXR_LOCKED_CHANNELS[eventKey] || []).includes(channelKey);

const WXR_DEFAULT_PREFS = {
  new_chat:       { in_app: true,  email: false, slack: false, teams: false, telegram: false },
  help_requested: { in_app: true,  email: true,  slack: false, teams: false, telegram: false },
  new_message:    { in_app: true,  email: false, slack: false, teams: false, telegram: false },
  new_lead:       { in_app: true,  email: true,  slack: false, teams: false, telegram: false },
};

const WXR_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function BillingCycleToggle({ billingCycle, setBillingCycle, t }) {
  return (
    <div className="wxr-billing-toggle" data-cycle={billingCycle}>
      <span className="wxr-billing-thumb" aria-hidden />
      <button
        type="button"
        className={billingCycle === "MONTH" ? "wxr-active" : ""}
        onClick={() => setBillingCycle("MONTH")}
      >
        {t("monthly")}
      </button>
      <button
        type="button"
        className={billingCycle === "YEAR" ? "wxr-active" : ""}
        onClick={() => setBillingCycle("YEAR")}
      >
        {t("yearly")}
      </button>
    </div>
  );
}

function PriceSwap({ showYearly, yearly, monthly, reducedMotion = false }) {
  const prefersReduced = typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const noAnim = reducedMotion || prefersReduced;

  return (
    <div className={`wxr-price-swap ${noAnim ? "wxr-no-anim" : ""}`}>
      <div className={`wxr-pane ${showYearly ? "wxr-show" : ""}`} aria-hidden={!showYearly}>
        {yearly}
      </div>
      <div className={`wxr-pane ${!showYearly ? "wxr-show" : ""}`} aria-hidden={showYearly}>
        {monthly}
      </div>
    </div>
  );
}

export default function WixRegister() {
  const [wsFailed, setWsFailed] = useState(false);
  const completedRef = useRef(false);
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [err, setErr] = useState("");
  const [code, setCode] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [url, setUrl] = useState("");
  const [siteId, setSiteId] = useState("");
  const [wixInstanceId, setWixInstanceId] = useState("");
  const [pct, setPct] = useState(0);
  const [bot, setBot] = useState(null);
  const [uid, setUid] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [token, setToken] = useState(null);
  const [isTraining, setIsTraining] = useState(false);

  const [requiresAccessToken, setRequiresAccessToken] = useState(false);
  const [appNotInstalled, setAppNotInstalled] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [authMode, setAuthMode] = useState('register');
  const plans = ["smart", "boost", "ultimo"];
  const priceMap = {
    MONTH: {
      smart: {
        priceId: "price_monthly_smart",
        labelKey: "plan_smart_price_monthly",
        id: "c9650231-29ac-4bb4-96cc-012ee2f33c8d",
      },
      boost: {
        priceId: "price_monthly_boost",
        labelKey: "plan_boost_price_monthly",
        id: "8a02ff6b-073f-4973-bc70-65efe4fc5177",
      },
      ultimo: {
        priceId: "price_monthly_ultimo",
        labelKey: "plan_ultimo_price_monthly",
        id: "e485cb14-03f4-48bd-a0ed-302fbef0a966",
      },
    },
    YEAR: {
      smart: {
        savePct: "save_pct_smart",
        priceId: "price_yearly_smart",
        labelKey: "plan_smart_price_yearly",
        yearlyOriginalKey: "plan_smart_price_yearly",
        yearlyDiscountedKey: "plan_smart_yearly_discounted",
        id: "c9650231-29ac-4bb4-96cc-012ee2f33c8d",
      },
      boost: {
        savePct: "save_pct_boost",
        priceId: "price_yearly_boost",
        labelKey: "plan_boost_price_yearly",
        yearlyOriginalKey: "plan_boost_price_yearly",
        yearlyDiscountedKey: "plan_boost_yearly_discounted",
        id: "8a02ff6b-073f-4973-bc70-65efe4fc5177",
      },
      ultimo: {
        savePct: "save_pct_ultimo",
        priceId: "price_yearly_ultimo",
        labelKey: "plan_ultimo_price_yearly",
        yearlyOriginalKey: "plan_ultimo_price_yearly",
        yearlyDiscountedKey: "plan_ultimo_yearly_discounted",
        id: "e485cb14-03f4-48bd-a0ed-302fbef0a966",
      },
    },
  };
  const planWrapperRef = useRef(null);
  const planCardRefs = useRef([]);
  const [activePlanIdx, setActivePlanIdx] = useState(1);
  const [billingCycle, setBillingCycle] = useState("YEAR");
  const defaultWidgetStyles = {
    themeColor: "#000000ff",
    headerColor: "#ffffff",
    hoverColor: "#232323ff",
    pulsing: true,
    widgetHorizontalAlignment: "right",
    widgetSize: 60,
    widgetBorderRadius: 50,
    headerText: "Chat with us",
    welcomeMessage: "Hi, how can I assist you?",
    icon: "",
  };
  const iconInputRef = useRef(null);
  const [widgetStyles, setWidgetStyles] = useState(defaultWidgetStyles);
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState("");
  const planPollRef = useRef(null);
  const [noContentFound, setNoContentFound] = useState(false);
  const [showNoContentModal, setShowNoContentModal] = useState(false);

  // Step 4 (engagement): notifications matrix + multi-email recipients
  const [notificationEmails, setNotificationEmails] = useState([""]);
  const [notifyPrefs, setNotifyPrefs] = useState(WXR_DEFAULT_PREFS);
  const [notifySaveAttempted, setNotifySaveAttempted] = useState(false);
  // Track whether the user has manually edited the first email slot.
  // While false, the prefill effect keeps slot 0 in sync with userEmail
  // (defends against early API responses delivering a partial value).
  const notifyEmailUserEditedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wixInstance = params.get("wix_instance_id");
    const codeParam = params.get("code");
    const errorParam = params.get("error");

    if (errorParam === "app_not_installed") {
      setAppNotInstalled(true);
    } else if (wixInstance) {
      setWixInstanceId(wixInstance);
      handleWixInstance(wixInstance);
    } else if (codeParam) {
      setCode(codeParam);
    } else {
      // TEMP: bypass session-expired screen for local testing
      setRequiresAccessToken(true);
    }
  }, []);

  const handleWixInstance = async (instanceId) => {
    try {
      setPageLoading(true);
      const r = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}auth/wix/continue_installation?wix_instance_id=${encodeURIComponent(
          instanceId
        )}`
      );
      const j = await r.json();

      if (j.url) {
        setUrl(j.url);
      }
      if (j.wix_instance_id) {
        setWixInstanceId(j.wix_instance_id);
      }

      console.log("[wix-register] resolve", {
        ok: r.ok,
        has_account: j.has_account,
        has_bot: j.has_bot,
        user_id: j.user_id,
        wix_instance_id: j.wix_instance_id,
      });
      if (r.ok && j.has_account) {
        // This Wix site already has a bot connected (e.g. the owner is just
        // updating the app). Don't re-run the create-a-bot onboarding — log
        // them in and send them straight to the portal dashboard.
        if (j.has_bot) {
          if (j.token) localStorage.setItem("token", j.token);
          if (j.user_id) localStorage.setItem("user_id", j.user_id);
          window.location.href = "/my-bots";
          return;
        }
        setStep(1);
        setUid(j.user_id);
        setToken(j.token);
        if (j.token) localStorage.setItem("token", j.token);
      }

      setUserEmail(j.user_email);
      setSiteId(j.site_id);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) return;

    (async () => {
      try {
        setPageLoading(true);

        const r = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}auth/wix/install_app?code=${encodeURIComponent(code)}`
        );
        const j = await r.json();

        if (j.requires_access_token) {
          setRequiresAccessToken(true);
          setPageLoading(false);
          return;
        }

        if (j.url) {
          setUrl(j.url);
        }
        if (j.wix_instance_id) {
          setWixInstanceId(j.wix_instance_id);
        }

        console.log("[wix-register] resolve", {
          ok: r.ok,
          has_account: j.has_account,
          has_bot: j.has_bot,
          user_id: j.user_id,
          wix_instance_id: j.wix_instance_id,
        });
        if (r.ok && j.has_account) {
          // This Wix site already has a bot connected (e.g. the owner is just
          // updating the app). Don't re-run the create-a-bot onboarding — log
          // them in and send them straight to the portal dashboard.
          if (j.has_bot) {
            if (j.token) localStorage.setItem("token", j.token);
            if (j.user_id) localStorage.setItem("user_id", j.user_id);
            window.location.href = "/my-bots";
            return;
          }
          setStep(1);
          setUid(j.user_id);
          setToken(j.token);
          if (j.token) localStorage.setItem("token", j.token);
        }

        setUserEmail(j.user_email);
        setSiteId(j.site_id);
      } catch (e) {
        console.error("Error in install_app:", e);
      } finally {
        setPageLoading(false);
      }
    })();
  }, [code]);

  useEffect(() => {
    if (!uid || !bot) return;

    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error("WebSocket URL missing");
      const url = raw.replace(/^http/, "ws");
      ws = new WebSocket(url);
    } catch (err) {
      console.error("WebSocket init failed:", err);
      setWsFailed(true);
      return;
    }

    ws.addEventListener("open", () => {
      ws.send(JSON.stringify({ type: "init", user_id: uid, token: localStorage.getItem('token') }));
    });

    ws.addEventListener("message", (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.bot_id && msg.bot_id !== bot) return;

        if (msg.percentage != null) setPct(msg.percentage);

        if (msg.status === "completed") {
          (async () => {
            setIsTraining(true);
            // Wix activation is handled server-side in generate_chatbot_task
            await fetchWidgetConfiguration(bot);
            setStep(2);
            setIsTraining(false);
            completedRef.current = true;
          })();
        }

        if (msg.status === "failed") {
          setErr(
            <>
              Something went wrong while creating your chatbot. Please try again. If this error persists, please reach out to us via{" "}
              <a href="mailto:build@ultimo-bots.com">build@ultimo-bots.com</a>.
            </>
          );
          setIsTraining(false);
          setPct(0);
          setBot(null);
        }

        if (msg.status === "no_content_found") {
          (async () => {
            setIsTraining(true);
            setNoContentFound(true);
            await fetchWidgetConfiguration(bot);
            setStep(2);
            setIsTraining(false);
            completedRef.current = true;
            setShowNoContentModal(true);
          })();
        }

      } catch (e) {
        console.error("WebSocket message parse error", e);
      }
    });

    ws.addEventListener("error", () => {
      if (!completedRef.current) setWsFailed(true);
    });

    return () => ws.close();
  }, [uid, bot]);

  const pollTimerRef = useRef(null);

  useEffect(() => {
    if (!wsFailed || !bot) return;

    function stopPolling() {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    async function pollOnce() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${encodeURIComponent(bot)}`
        );
        if (!res.ok) throw new Error("status fetch failed");
        const data = await res.json();

        if (data.creation_percentage != null) setPct(data.creation_percentage);

        if (data.creation_percentage >= 100) {
          // Wix activation is handled server-side in generate_chatbot_task
          stopPolling();
          await fetchWidgetConfiguration(bot);
          setStep(2);
          setIsTraining(false);
        }

        if (data.status === "failed") {
          stopPolling();
          setErr(
            <>
              Something went wrong while creating your chatbot. Please try again. If this error persists, please reach out to us via{" "}
              <a href="mailto:build@ultimo-bots.com">build@ultimo-bots.com</a>.
            </>
          );
          setIsTraining(false);
          setPct(0);
          setBot(null);
        }

        if (data.status === "no_content_found") {
          stopPolling();
          setNoContentFound(true);
          await fetchWidgetConfiguration(bot);
          setStep(2);
          setIsTraining(false);
          setShowNoContentModal(true);
        }

      } catch (e) {
        console.error("Polling error:", e);
        setErr("Could not retrieve progress updates. Please stay on this page; the assistant will appear in a moment.");
        stopPolling();
      }
    }

    pollTimerRef.current = setInterval(pollOnce, 1000);
    pollOnce();

    return () => stopPolling();
  }, [wsFailed, bot]);

  useEffect(() => {
    if (step !== 2) return;
    const wrapper = planWrapperRef.current;
    const cards   = planCardRefs.current;
    if (!wrapper || cards.length === 0) return;
    const GAP = 20;
    const updateActiveIdx = () => {
      const cardWidth = cards[0].offsetWidth;
      const idx = Math.round(wrapper.scrollLeft / (cardWidth + GAP));
      setActivePlanIdx(Math.min(cards.length - 1, Math.max(0, idx)));
    };

    updateActiveIdx();
    wrapper.addEventListener("scroll", updateActiveIdx, { passive: true });

    return () => wrapper.removeEventListener("scroll", updateActiveIdx);
  }, [step]);

  useEffect(() => () => stopPlanPolling(), []);
  useEffect(() => { if (step !== (noContentFound ? 4 : 5)) stopPlanPolling(); }, [step, noContentFound]);

  const fetchWidgetConfiguration = useCallback(
    async (botId) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${botId}`
        );
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();

        const fetchedStyles = {
          themeColor: data.theme_color,
          hoverColor: data.button_hover_color,
          headerColor: data.header_font_color,
          pulsing: data.pulsing,
          widgetHorizontalAlignment: data.widget_horizontal_alignment,
          widgetBorderRadius: data.widget_border_radius ?? 50,
          widgetSize: data.widget_size ?? 70,
          headerText: data.header_text || "Chat with us",
          welcomeMessage: Array.isArray(data.welcome_message)
            ? (data.welcome_message[0] || "Hi, how can I assist you?")
            : (data.welcome_message || "Hi, how can I assist you?"),
          icon: data.widget_icon_path || "",
        };

        setWidgetStyles(fetchedStyles);
      } catch (err) {
        console.error("Error fetching widget configuration:", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  function base64ToFile(base64, filename) {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8 = new Uint8Array(n);
    while (n--) u8[n] = bstr.charCodeAt(n);
    return new File([u8], filename, { type: mime });
  }

  async function uploadImage(base64, type) {
    const fd = new FormData();
    fd.append("file", base64ToFile(base64, `${type}-${Date.now()}.png`));
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}upload_image/`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) throw new Error(`Failed to upload ${type}. Status: ${res.status}`);
    const j = await res.json();
    return j.file_path;
  }

  function handleIconUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      handleStyleChange("icon", ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  function handleRemoveIcon() {
    handleStyleChange("icon", "");
  }

  // Default the first notification email slot to the user's account email
  // once it's known. Keeps slot 0 synced with userEmail until the user edits it,
  // so a late/longer userEmail update overrides any earlier partial value.
  useEffect(() => {
    if (!userEmail) return;
    if (notifyEmailUserEditedRef.current) return;
    setNotificationEmails((prev) => {
      const next = prev.length > 0 ? prev.slice() : [""];
      next[0] = userEmail;
      return next;
    });
  }, [userEmail]);

  // Whether email is needed by at least one event (controls validation).
  const wxrEmailRequired = WXR_NOTIFY_EVENTS.some(
    (ev) => !wxrIsChannelLocked(ev.key, "email") && Boolean(notifyPrefs[ev.key]?.email)
  );
  const wxrCleanedEmails = (notificationEmails || [])
    .map((e) => String(e).trim())
    .filter((e) => e.length > 0);
  const wxrInvalidEmails = wxrCleanedEmails.filter((e) => !WXR_EMAIL_REGEX.test(e));
  const wxrEmailMissing = wxrEmailRequired && wxrCleanedEmails.length === 0;
  const wxrEmailHasError = wxrEmailMissing || wxrInvalidEmails.length > 0;

  async function saveEngagementSettings() {
    if (!bot) return false;
    setNotifySaveAttempted(true);
    if (wxrEmailHasError) return false;
    setErr('');
    setLoading(true);
    try {
      const validEmails = wxrCleanedEmails.filter((e) => WXR_EMAIL_REGEX.test(e));

      // Build per-section recipient mirrors (matches notificationCenter
      // save shape). A section gets the email list only if at least one of
      // its events actually has email enabled — otherwise empty.
      const sectionNeedsEmail = (sectionKey) =>
        WXR_NOTIFY_EVENTS.some(
          (ev) =>
            ev.section === sectionKey &&
            !wxrIsChannelLocked(ev.key, "email") &&
            Boolean(notifyPrefs[ev.key]?.email)
        );

      const notifyPayload = {
        prefs: notifyPrefs,
        emails_by_section: {
          live_chat: sectionNeedsEmail("live_chat") ? validEmails : [],
          leads: sectionNeedsEmail("leads") ? validEmails : [],
        },
        // Slack / Teams / Telegram recipients are configured later in the
        // portal — we send empty arrays here so the backend doesn't drop
        // the prefs flags.
        slack_webhooks_by_section: { live_chat: [], leads: [] },
        teams_webhooks_by_section: { live_chat: [], leads: [] },
        telegram_chat_ids_by_section: { live_chat: [], leads: [] },
      };

      try {
        const res = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}notification_settings/${encodeURIComponent(bot)}`,
          { method: "PUT", body: JSON.stringify(notifyPayload) }
        );
        if (!res || !res.ok) console.error("notification_settings PUT failed:", res?.status);
      } catch (innerErr) {
        console.error("notification_settings PUT failed:", innerErr);
      }
      return true;
    } catch (e) {
      console.error(e);
      return true; // never block onboarding on this teaser step
    } finally {
      setLoading(false);
    }
  }

  async function saveWidgetConfiguration() {
    if (!bot) return false;
    setErr('');
    setLoading(true);
    try {
      let iconPath = widgetStyles.icon;
      if (iconPath && iconPath.startsWith("data:image/")) {
        iconPath = await uploadImage(iconPath, "icon");
      }

      const updates = {
        theme_color: widgetStyles.themeColor,
        header_font_color: widgetStyles.headerColor,
        button_hover_color: widgetStyles.hoverColor,
        pulsing: widgetStyles.pulsing,
        widget_horizontal_alignment: widgetStyles.widgetHorizontalAlignment,
        widget_size: widgetStyles.widgetSize,
        widget_border_radius: widgetStyles.widgetBorderRadius,
        header_text: widgetStyles.headerText,
        welcome_message: [widgetStyles.welcomeMessage],
        widget_icon_path: iconPath || "",
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}widget_configuration/${encodeURIComponent(bot)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        }
      );
      if (!res.ok) throw new Error("Widget configuration failed");
      // Persist resolved icon path in state (replace data URL)
      if (iconPath !== widgetStyles.icon) {
        handleStyleChange("icon", iconPath || "");
      }
      return true;
    } catch (e) {
      console.error(e);
      setErr("Couldn't save your design. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handlePw(e) {
    e.preventDefault();
    setErr('');
    const email = (userEmail || '').trim();
    const first = (firstName || '').trim();
    const pass1 = pw || '';
    const pass2 = pw2 || '';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setErr(t('email_required') || 'Please enter your email.');
      return;
    }
    if (!emailRegex.test(email)) {
      setErr(t('email_invalid') || 'Please enter a valid email address.');
      return;
    }
    if (!first) {
      setErr(t('first_name_required') || 'Please enter your first name.');
      return;
    }
    if (pass1.length < 8) {
      setErr(t('password_too_short') || 'Password must be at least 8 characters long.');
      return;
    }
    if (pass1 !== pass2) {
      setErr(t('passwords_do_not_match') || 'Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}register/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email,
          password: pass1,
          given_name: first,
          send_email: true,
          wix_instance_id: wixInstanceId
        }),
      });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        if (r.status === 400 && errData.detail === "Email already registered") {
          setErr(t('email_already_registered') || 'This email is already registered. Please use a different email or log in.');
        } else {
          setErr(t('set_password_error') || 'Could not create your account. Please try again.');
        }
        return;
      }
      const j = await r.json();
      setUid(j.user_id);
      setToken(j.token);
      if (j.token) localStorage.setItem("token", j.token);
      setStep(1);
    } catch {
      setErr(t('set_password_error') || 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr('');
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail,
          password: pw,
          wix_instance_id: wixInstanceId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setUid(data.user_id);
        setToken(data.token);
        if (data.token) localStorage.setItem("token", data.token);
        if (data.first_name) setFirstName(data.first_name);
        setStep(1);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setErr(errorData.detail || t('invalid_credentials') || 'Invalid email or password.');
      }
    } catch {
      setErr(t('login_error') || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function extractDomainName(inputUrl) {
    try {
      const prefixedUrl = inputUrl.startsWith('http') ? inputUrl : 'https://' + inputUrl;
      const parsedUrl = new URL(prefixedUrl);

      let host = parsedUrl.hostname;
      if (host.startsWith('www.')) host = host.slice(4);
      const parts = host.split('.');

      if (parts.length >= 2) {
        return parts[parts.length - 2];
      }

      return host;
    } catch {
      return 'bot';
    }
  }

  async function isValidUrlRemote(urlToCheck) {
    if (!urlToCheck) return { valid: false, reason: "Please enter your website URL." };

    const trimmed = urlToCheck.trim();
    const encodedUrl = encodeURIComponent(trimmed);

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}url/check?url=${encodedUrl}`);
      if (!response.ok) {
        return { valid: false, reason: "Something went wrong while checking your URL. Please try again." };
      }

      const result = await response.json();

      if (result.reachable !== true) {
        return { valid: false, reason: "We couldn't reach this URL. Please check the address and make sure your site is published." };
      }

      if (result.is_valid !== "yes") {
        return { valid: false, reason: result.decline_reason || "This doesn't appear to be a valid business website. Please enter your company's URL." };
      }

      return { valid: true };
    } catch (error) {
      console.error("Error checking URL validity:", error);
      return { valid: false, reason: "Something went wrong. Please try again." };
    } finally {
      setLoading(false);
    }
  }

  async function createBot() {
    setErr('');

    if (!url.trim()) {
      setErr('Please enter your site URL.');
      return;
    }

    const result = await isValidUrlRemote(url);
    if (!result.valid) {
      setErr(result.reason);
      return;
    }

    const domainBotName = extractDomainName(url);

    const fd = new FormData();
    fd.append('url', url);
    fd.append('bot_name', domainBotName);
    fd.append('language', 'English');
    fd.append('user_id', uid);
    fd.append('send_email_to_user', false);
    fd.append('javascript_rendering', false);
    fd.append('wix_instance_id', wixInstanceId);

    try {
      setIsTraining(true);
      setLoading(true);

      const r = await fetch(`${process.env.REACT_APP_API_BASE_URL}create_chatbot`, {
        method: 'POST',
        body: fd,
      });

      if (!r.ok) throw new Error();

      const j = await r.json();
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'bot_creation_started' });
      }
      setBot(j.bot_id);
    } catch (err) {
      console.error(err);
      setErr('Failed to create chatbot.');
      setIsTraining(false);
    } finally {
      setLoading(false);
    }
  }

  async function handleTryAnotherUrl() {
    setShowNoContentModal(false);

    // Delete the bot in the background
    if (bot && token) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}bots/${encodeURIComponent(bot)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.error('Failed to delete bot:', e);
      }
    }

    // Reset all bot-creation state
    setBot(null);
    setPct(0);
    setNoContentFound(false);
    setIsTraining(false);
    setErr('');
    setWidgetStyles(defaultWidgetStyles);
    completedRef.current = false;
    setUrl('');
    setStep(1);
  }

  const handleStyleChange = (key, value) => {
    setWidgetStyles((prevStyles) => ({ ...prevStyles, [key]: value }));
  };

  const handlePlanClick = (planKey) => {
    if (!siteId) {
      console.error("Missing siteId");
      return;
    }
    const selectedPriceId = priceMap[billingCycle][planKey].id;

    const checkoutUrl =
      `https://manage.wix.com/app-pricing-plans/${process.env.REACT_APP_WIX_APP_ID}/plan/${selectedPriceId}/payment-cycle/${billingCycle}/order-checkout?meta-site-id=${siteId}&origin=manage_apps_bizmgr`;

    // Open checkout in new tab
    window.open(checkoutUrl, "_blank", "noopener,noreferrer");

    // Show loader & start polling *now* in the current tab
    startPlanPolling();
  };

  async function fetchBotPlanName(currentBotId = bot) {
    if (!currentBotId) throw new Error("Missing bot id");
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}bot?bot_id=${encodeURIComponent(currentBotId)}&include_plan=true`
    );
    if (!res.ok) throw new Error(`bot fetch failed: ${res.status}`);
    const data = await res.json();
    return data?.subscription_plan?.name ?? "Free";
  }

  function stopPlanPolling() {
    if (planPollRef.current) {
      clearInterval(planPollRef.current);
      planPollRef.current = null;
    }
    setIsCheckingPlan(false);
  }

  function startPlanPolling() {
    if (planPollRef.current || !bot) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);

    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName(bot);
        if (name && name !== "Free") {
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'free_trial_started' });
          }
          stopPlanPolling();
          setStep(noContentFound ? 4 : 5);
        }
      } catch (e) {
        setPlanCheckError('Checking your subscription...');
      }
    }, 2000);
  }

  const slides = [
    /* step 0 - register / login */
    (
      authMode === 'register'
        ? (
          <div className='wxr-card-button-wrapper'>
            <form className="wxr-card" autoComplete="on" onSubmit={handlePw}>
              <h2>Welcome!</h2>
              <p className="wxr-sub">
                Create an account to access the <strong>Ultimo Bots Portal</strong> and manage your chatbot.
              </p>

              <input
                className="wxr-register-input-text"
                type="text"
                name="firstName"
                autoComplete="given-name"
                placeholder={t('first_name')}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
              />

              <input
                className="wxr-register-input-text"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('email')}
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                required
              />

              <input
                className="wxr-register-input-text"
                type="password"
                name="new-password"
                autoComplete="new-password"
                placeholder={t('password')}
                value={pw}
                onChange={e => setPw(e.target.value)}
                minLength={8}
                required
              />

              <input
                className="wxr-register-input-text"
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                placeholder={t('confirm_password')}
                value={pw2}
                onChange={e => setPw2(e.target.value)}
                minLength={8}
                required
              />

              {err && <div className="wxr-err">{err}</div>}

              <p className="wxr-link" style={{ marginTop: 12 }}>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => { setErr(''); setAuthMode('login'); }}
                >
                  Already have an account? Login
                </span>
              </p>
            </form>

            <button className="wxr-btn" type="button" disabled={loading} onClick={handlePw}>
              {t('get_started')} {loading && <span className="wxr-spin" />}
            </button>
          </div>
        )
        : (
          <div className='wxr-card-button-wrapper'>
            <form className="wxr-card" autoComplete="on" onSubmit={handleLogin}>
              <h2>Welcome back!</h2>
              <p className="wxr-sub">
                Log in to the <strong>Ultimo Bots Portal</strong> to manage your chatbot.
              </p>

              <input
                className="wxr-register-input-text"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('email')}
                value={userEmail}
                onChange={e => setUserEmail(e.target.value)}
                required
              />

              <input
                className="wxr-register-input-text"
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder={t('password')}
                value={pw}
                onChange={e => setPw(e.target.value)}
                required
              />

              {err && <div className="wxr-err">{err}</div>}

              <p className="wxr-link" style={{ marginTop: 12 }}>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => { setErr(''); setPw(''); setPw2(''); setAuthMode('register'); }}
                >
                  New? Register Now!
                </span>
              </p>
            </form>

            <button className="wxr-btn" type="button" disabled={loading} onClick={handleLogin}>
              {t('login') || 'Log in'} {loading && <span className="wxr-spin" />}
            </button>
          </div>
        )
    ),

    /* step 1 - url */
    <div className='wxr-card-button-wrapper'>
      <form className={`wxr-card${isTraining ? ' wxr-building' : ''}`} onSubmit={e=>e.preventDefault()}>
        <h2>Create your chatbot</h2>
        <p className="wxr-sub">
          We'll train your bot using the content from your website.<br />
          You can add more sources anytime.
        </p>
        <input
          className={`wxr-register-input-text${err ? ' wxr-input-error' : ''}`}
          type="text"
          placeholder="Enter your site URL"
          value={url}
          onChange={e => setUrl(e.target.value)}
          required
          disabled={isTraining}
        />

        {isTraining && (
          <div className='wxr-chatbot-loading-container'>
            <Atom color="#5616ea" size="medium" speedPlus={'0'} />
            {(() => {
              const rounded = Math.round(pct);
              let label;

              if (rounded <= 5) {
                label = t("training_starting");
              } else if (rounded <= 50) {
                label = t("training_extracting");
              } else if (rounded <= 80) {
                label = t("training_embedding");
              } else {
                label = t("training_finalizing");
              }

              return `${label}: ${rounded}%`;
            })()}
          </div>
        )}
        {err && <div className="wxr-err" style={{marginTop: '0px'}}>{err}</div>}
        <div className="wxr-privacy-note">
          <br />Your data is safe and privately stored in Switzerland.
        </div>
      </form>
      <button className="wxr-btn" type="button" onClick={createBot} disabled={loading || isTraining}>
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create Chatbot'}
      </button>
    </div>,

    /* step 2 - customise */
    <div className='wxr-card-button-wrapper'>
      <div className='wxr-preview-parent-container'>
        <div className='wxr-preview-header-text'>
          <div className='wxr-header-back-button'>
            {/* <BackButton step={step} setStep={setStep} /> */}
            <h2>Customize Your AI Assistant</h2>
          </div>
          <p className="wxr-sub" style={{marginBottom: "0px"}}>
            Customize the design to perfectly match your brand. In our portal, you'll unlock even more options.
          </p>
        </div>
        <div className="wxr-preview-card">
          <div className='wxr-user-settings'>
            <div className="wxr-control-group">
              <TextField
                id="wxr-header-text-input"
                label="Header text"
                value={widgetStyles.headerText}
                onChange={(e) => handleStyleChange("headerText", e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 60 }}
                helperText={`${(widgetStyles.headerText || "").length}/60`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    mr: 0,
                    color:
                      (widgetStyles.headerText || "").length >= 60
                        ? "error.main"
                        : "text.secondary",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    border: "1px solid #515151",
                  },
                  "& .MuiInputLabel-root": {
                    color: "gray",
                    "&.Mui-focused": { color: "#5616ea" },
                  },
                }}
              />
            </div>
            <div className="wxr-control-group">
              <TextField
                id="wxr-welcome-message-input"
                label="Welcome message"
                value={widgetStyles.welcomeMessage}
                onChange={(e) => handleStyleChange("welcomeMessage", e.target.value)}
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 300 }}
                helperText={`${(widgetStyles.welcomeMessage || "").length}/300`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "right",
                    mr: 0,
                    color:
                      (widgetStyles.welcomeMessage || "").length >= 300
                        ? "error.main"
                        : "text.secondary",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    border: "1px solid #515151",
                  },
                  "& .MuiInputLabel-root": {
                    color: "gray",
                    "&.Mui-focused": { color: "#5616ea" },
                  },
                  "& .MuiOutlinedInput-root textarea": {
                    overflowY: "auto !important",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#c9c2e6 transparent",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c9c2e6",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": { background: "#5616ea" },
                  },
                }}
              />
            </div>
            <div className="wxr-control-group">
              <div className="wxr-color-picker-container">
                <label className="wxr-widget-setting-title" htmlFor="themeColorPicker">Theme Color:</label>
                <input
                  id="themeColorPicker"
                  type="color"
                  value={widgetStyles.themeColor}
                  onChange={(e) => handleStyleChange("themeColor", e.target.value)}
                />
              </div>
            </div>
            <div className="wxr-control-group">
              <div className="wxr-color-picker-container">
                <label className="wxr-widget-setting-title">Header Font Color:</label>
                <input
                  type="color"
                  value={widgetStyles.headerColor}
                  onChange={(e) =>
                    handleStyleChange("headerColor", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="wxr-control-group">
              <div className="wxr-color-picker-container">
                <label className="wxr-widget-setting-title">Widget Icon:</label>
                <div className="wxr-logo-upload">
                  {widgetStyles.icon ? (
                    <>
                      <img
                        src={widgetStyles.icon}
                        alt="Widget icon preview"
                        className="wxr-logo-preview"
                      />
                      <button
                        type="button"
                        className="wxr-logo-upload-btn"
                        onClick={() => iconInputRef.current && iconInputRef.current.click()}
                      >
                        <FontAwesomeIcon icon={faUpload} />
                        <span>Replace</span>
                      </button>
                      <button
                        type="button"
                        className="wxr-logo-remove-btn"
                        onClick={handleRemoveIcon}
                        aria-label="Remove icon"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="wxr-logo-upload-btn"
                      onClick={() => iconInputRef.current && iconInputRef.current.click()}
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      <span>Upload</span>
                    </button>
                  )}
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleIconUpload}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="wxr-divider"></div>
          <div className="wxr-preview-area">
            <div
              className={`wxr-chat-widget-icon
                          ${widgetStyles.pulsing ? "wxr-pulsing" : ""}
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "wxr-align-left" : "wxr-align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "wxr-elevated" : "wxr-bottom"}`}
              style={{
                width:  `${widgetStyles.widgetSize}px`,
                height: `${widgetStyles.widgetSize}px`,
                borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                backgroundColor: widgetStyles.icon ? "transparent" : widgetStyles.themeColor,
                flex: "0 0 auto",
                display: "flex",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              {widgetStyles.icon ? (
                <img
                  src={widgetStyles.icon}
                  alt="Widget Icon"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    borderRadius: `${widgetStyles.widgetBorderRadius}%`,
                  }}
                />
              ) :
                <div style={{display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "transparent", paddingTop: "3px"}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="65%" viewBox="0 0 24 24" fill="white" className="wxr-size-6">
                    <path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" />
                  </svg>
                </div>
              }
            </div>

            <div
              className={`wxr-chat-window wxr-show
                          ${widgetStyles.widgetHorizontalAlignment === "left" ? "wxr-align-left" : "wxr-align-right"}
                          ${widgetStyles.widgetVerticalAlignment === "elevated" ? "wxr-elevated" : "wxr-bottom"}`}
              style={{
                width: "280px",
                height: "380px",
              }}
            >
              <div
                className="wxr-chat-header"
                style={{ backgroundColor: widgetStyles.themeColor }}
              >
                <div className="wxr-chat-header-title">
                  <div className="wxr-logo-message-container">
                    <span
                      className="wxr-onboarding-chat-title"
                      style={{ color: widgetStyles.headerColor }}
                    >
                      {widgetStyles.headerText || "Chat with us"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="wxr-onboarding-widget-close-button"
                    style={{ color: widgetStyles.headerColor }}
                    aria-label="Close chat"
                  >
                    <FontAwesomeIcon icon={faXmark}/>
                  </button>
                </div>
              </div>

              <div className="wxr-chat-body">
                <div className="wxr-widget-message wxr-widget-bot-message">
                  {widgetStyles.welcomeMessage || "Hi, this is a preview of your chat widget. Customize it further in the Ultimo Bots Portal!"}
                </div>
              </div>

              <div className="wxr-chat-footer">
                <div className="wxr-powered-by">
                  <span className="wxr-powered-by-text">Powered by Ultimo Bots</span>
                </div>

                <div className="wxr-input-send-container">
                  <input
                    type="text"
                    className="wxr-chat-input"
                    placeholder=""
                    disabled
                  />
                  <button
                    type="button"
                    className="wxr-onboarding-send-message"
                    style={{
                      backgroundColor: widgetStyles.themeColor,
                      color: widgetStyles.headerColor,
                    }}
                  >
                    <i className="wxr-widget-send-icon"><FontAwesomeIcon icon={faArrowUp}/></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .wxr-onboarding-send-message:hover {
              background-color: ${widgetStyles.hoverColor} !important;
            }
            .wxr-preview-area .wxr-loading-dots div {
              background-color: ${widgetStyles.themeColor} !important;
            }
          `}</style>
        </div>
        {err && <div className="wxr-err">{err}</div>}
      </div>
      <button
        className="wxr-btn"
        disabled={loading}
        onClick={async () => {
          const ok = await saveWidgetConfiguration();
          if (ok) setStep(3);
        }}
      >
        <div className='wxr-portal-button-content'>
          <FontAwesomeIcon icon={faCircleRight} />
          {noContentFound ? 'Continue' : 'Test Your Chatbot'} {loading && <span className="wxr-spin" />}
        </div>
      </button>
    </div>,

    /* step 3 - test (excluded when noContentFound) */
    ...(noContentFound ? [] : [
    <div key="test-step" className='wxr-card-button-wrapper'>
      <div className="wxr-chatbot-card">
        <div className='wxr-test-assistant-text-container'>
          <div className='wxr-header-back-button'>
            <BackButton step={step} setStep={setStep} />
            <h2>Test Your Chatbot</h2>
          </div>
          <p className="wxr-sub" style={{marginBottom: '10px'}}>
            {t("test_assistant_desc")}
          </p>
        </div>
        {step >= 2 && (
          <BotChatStep
            bot={bot}
            themeColor={widgetStyles.themeColor}
          />
        )}
      </div>
      <button
        className="wxr-btn"
        disabled={loading}
        onClick={() => {
          setStep(4);
        }}
        style={{ maxWidth: "260px" }}
      >
        <div className="wxr-portal-button-content">
          <FontAwesomeIcon icon={faCircleRight} />
          Set Up Alerts {loading && <span className="wxr-spin" />}
        </div>
      </button>
    </div>
    ]),

    /* step 4 - notification preferences (engagement) */
    <div key="engagement-step" className='wxr-card-button-wrapper'>
      <div className='wxr-engagement-card'>
        <div className='wxr-preview-header-text'>
          <div className='wxr-header-back-button'>
            <BackButton step={step} setStep={setStep} />
            <h2>Never Miss an Important Moment</h2>
          </div>
          <p className="wxr-sub" style={{marginBottom: '14px'}}>
            Pick which events you want to hear about and where. You can fine-tune everything (including Telegram, Slack &amp; Teams setup) anytime in the portal.
          </p>
        </div>

        {/* ── Notification matrix ───────────────────────────── */}
        <div className="wxr-notify-matrix">
          <div className="wxr-notify-matrix-row wxr-notify-matrix-header">
            <div className="wxr-notify-matrix-event-col" />
            {WXR_NOTIFY_CHANNELS.map((ch) => (
              <div key={ch.key} className="wxr-notify-matrix-channel-col">
                {ch.BrandIcon ? (
                  <span className="wxr-notify-matrix-channel-icon wxr-notify-matrix-channel-icon-brand">
                    <ch.BrandIcon size="16px" />
                  </span>
                ) : (
                  <FontAwesomeIcon
                    icon={ch.icon}
                    className="wxr-notify-matrix-channel-icon"
                    style={{ color: ch.color }}
                  />
                )}
                <span className="wxr-notify-matrix-channel-label-row">
                  <span className="wxr-notify-matrix-channel-label">{ch.label}</span>
                  {ch.comingSoon && (
                    <span className="wxr-notify-matrix-coming-soon">Soon</span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {WXR_NOTIFY_EVENTS.map((ev) => {
            const row = notifyPrefs[ev.key] || {};
            return (
              <div
                key={ev.key}
                className={`wxr-notify-matrix-row ${ev.highlight ? 'wxr-notify-matrix-row-highlight' : ''}`}
              >
                <div className="wxr-notify-matrix-event-col">
                  <div className="wxr-notify-matrix-event-title">
                    <FontAwesomeIcon icon={ev.icon} className="wxr-notify-matrix-event-icon" />
                    {ev.title}
                  </div>
                  <div className="wxr-notify-matrix-event-desc">{ev.desc}</div>
                </div>
                {WXR_NOTIFY_CHANNELS.map((ch) => {
                  const locked = wxrIsChannelLocked(ev.key, ch.key) || Boolean(ch.comingSoon);
                  const checked = locked ? Boolean(row[ch.key]) && !ch.comingSoon : Boolean(row[ch.key]);
                  const checkbox = (
                    <Checkbox
                      checked={checked}
                      disabled={locked}
                      onChange={() => {
                        setNotifySaveAttempted(false);
                        setNotifyPrefs((prev) => ({
                          ...prev,
                          [ev.key]: {
                            ...(prev[ev.key] || {}),
                            [ch.key]: !prev[ev.key]?.[ch.key],
                          },
                        }));
                      }}
                      sx={{
                        '&.Mui-checked': { color: '#5616ea' },
                        '&:hover': { backgroundColor: '#5616ea15' },
                        '&.Mui-disabled': { color: '#e0e0e6' },
                        padding: '4px',
                      }}
                    />
                  );
                  return (
                    <div key={ch.key} className="wxr-notify-matrix-channel-col">
                      {locked ? (
                        <Tooltip
                          title={
                            ch.comingSoon
                              ? 'This channel is coming soon'
                              : 'Not available for this notification type'
                          }
                          arrow
                        >
                          <span style={{ cursor: 'not-allowed', display: 'inline-flex' }}>
                            <span style={{ pointerEvents: 'none' }}>{checkbox}</span>
                          </span>
                        </Tooltip>
                      ) : (
                        checkbox
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── More channels — available in the portal ────────── */}
        <div className="wxr-notify-portal-channels">
          <span className="wxr-notify-portal-channels-label">
            Also available in the portal
          </span>
          <span className="wxr-notify-portal-chips">
            <span className="wxr-notify-portal-chip">
              <TelegramBrandIcon size={16} />
              Telegram
            </span>
            <span className="wxr-notify-portal-chip">
              <SlackBrandIcon size={16} />
              Slack
            </span>
            <span className="wxr-notify-portal-chip">
              <TeamsBrandIcon size={16} />
              Teams
              <span className="wxr-notify-matrix-coming-soon">Soon</span>
            </span>
          </span>
        </div>

        {/* ── Email recipients ──────────────────────────────── */}
        {wxrEmailRequired && (
          <div className="wxr-notify-recipients">
            <div className="wxr-notify-recipients-heading">
              <FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} />
              <span>Email recipients</span>
            </div>

            <div className="wxr-notify-emails-list">
              {(notificationEmails.length > 0 ? notificationEmails : [""]).map((value, idx) => {
                const list = notificationEmails.length > 0 ? notificationEmails : [""];
                const trimmed = String(value).trim();
                const showError = notifySaveAttempted && trimmed.length > 0 && !WXR_EMAIL_REGEX.test(trimmed);
                return (
                  <div key={idx} className="wxr-notify-email-row">
                    <TextField
                      label={`Email ${idx + 1}`}
                      value={value}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (idx === 0) notifyEmailUserEditedRef.current = true;
                        setNotifySaveAttempted(false);
                        setNotificationEmails((prev) => {
                          const next = prev.slice();
                          next[idx] = v;
                          return next;
                        });
                      }}
                      type="text"
                      fullWidth
                      variant="outlined"
                      error={showError}
                      placeholder="you@example.com"
                      InputProps={{
                        endAdornment:
                          list.length > 1 ? (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Remove"
                                edge="end"
                                size="small"
                                sx={{ color: 'text.secondary' }}
                                onClick={() => {
                                  setNotifySaveAttempted(false);
                                  setNotificationEmails((prev) => {
                                    const next = prev.filter((_, i) => i !== idx);
                                    return next.length > 0 ? next : [""];
                                  });
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
                        '& .MuiInputLabel-root': {
                          color: 'gray',
                          '&.Mui-focused': { color: '#5616ea' },
                        },
                      }}
                    />
                  </div>
                );
              })}
            </div>
            {notificationEmails.length < 3 && (
              <button
                type="button"
                className="wxr-notify-add-email-btn add-email-button"
                onClick={() => {
                  setNotifySaveAttempted(false);
                  setNotificationEmails((prev) => (prev.length >= 3 ? prev : [...prev, ""]));
                }}
                title="Add another email"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
            {notifySaveAttempted && wxrEmailHasError && (
              <div className="wxr-notify-error">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span>
                  {wxrEmailMissing
                    ? 'Add at least one email address (or turn off the email column above).'
                    : 'One or more email addresses look invalid.'}
                </span>
              </div>
            )}
          </div>
        )}

        {err && <div className="wxr-err">{err}</div>}
      </div>
      <button
        className="wxr-btn"
        disabled={loading}
        onClick={async () => {
          const ok = await saveEngagementSettings();
          if (ok) setStep(noContentFound ? 4 : 5);
        }}
      >
        <div className='wxr-portal-button-content'>
          <FontAwesomeIcon icon={faCircleRight} />
          {t('start_free_trial')} {loading && <span className="wxr-spin" />}
        </div>
      </button>
    </div>,

    /* step 3/4 - choose subscription */
    <div className="wxr-subscription-section">
      <div className="wxr-header-back-button">
        {isCheckingPlan ? (
          <button className="wxr-lp-back-btn" type="button" onClick={() => stopPlanPolling()}>
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginLeft: "1px" }} />
          </button>
        ) : (
          <BackButton step={step} setStep={setStep} />
        )}
        <h2>Start Your 7-Day Free Trial</h2>
      </div>

      <p className="wxr-sub" style={{ marginBottom: '20px' }}>
        Experience the full power of Ultimo Bots - completely free for 7 days.<br />
        <strong>94%</strong> of users stick with us after the free trial. Find out why.
      </p>

      {/* Either loader OR pricing UI */}
      {isCheckingPlan ? (
        /* ===== Loader mode ===== */
        <div className="wxr-plan-wait-card" style={{ margin: '0 auto 16px', maxWidth: 520 }}>
          <Atom color="#5616ea" size="medium" speedPlus={'0'} />
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <div style={{ fontWeight: 600 }}>Waiting for your plan to activate...</div>
            <div style={{ opacity: 0.8, marginTop: 6 }}>
              Complete the checkout in the other tab. We'll detect it automatically.
            </div>
            {planCheckError && (
              <div className="wxr-err" style={{ marginTop: 10 }}>{planCheckError}</div>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button
                type="button"
                className="wxr-btn"
                onClick={async () => {
                  try {
                    const name = await fetchBotPlanName();
                    if (name !== "Free") {
                      stopPlanPolling();
                      setStep(noContentFound ? 4 : 5);
                    }
                  } catch {
                    setPlanCheckError('Still checking your subscription...');
                  }
                }}
              >
                Refresh now
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ===== Pricing UI mode ===== */
        <>
          {/* Monthly / Yearly toggle */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: '10px' }}>
            <BillingCycleToggle
              billingCycle={billingCycle}
              setBillingCycle={setBillingCycle}
              t={t}
            />
          </div>

          <div className="wxr-plan-slider-wrapper" ref={planWrapperRef}>
            <div className="wxr-plan-slider">
              {/* SMART */}
              <div
                className="wxr-plan-card"
                ref={(el) => (planCardRefs.current[0] = el)}
              >
                <h3 style={{ margin: '0' }}>{t("plan_smart")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="wxr-yearly-crossout">
                        <span className="wxr-wix-plan-price">
                          {t(priceMap.YEAR.smart.yearlyDiscountedKey)}
                        </span>
                        <span className="wxr-old-price">{t(priceMap.YEAR.smart.yearlyOriginalKey)}</span>
                        <span className="wxr-deal-tag">{t(priceMap.YEAR.smart.savePct)}</span>
                      </div>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wxr-wix-plan-price">{t(priceMap.MONTH.smart.labelKey)}</p>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="wxr-plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>500</strong> AI responses/month
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>200</strong> Knowledge Sources
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>3</strong> Custom Behaviors
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Chat
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Lead Capture
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Notifications
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Messaging
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-facebook">Facebook Messenger</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-instagram">Instagram DMs</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-whatsapp">WhatsApp</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <SlackLogo size={18} className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-slack">Slack</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <FontAwesomeIcon icon={faTelegram} className="wxr-feature-platform-icon" style={{ color: '#229ED9', fontSize: '18px' }} />
                      <span className="wxr-icon-tooltip wxr-telegram">Telegram</span>
                    </span>
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Sync data
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/notion-logo.png" alt="Notion" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-notion">Notion</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-google-drive">Google Drive</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-onedrive">Microsoft OneDrive</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faXmark} className="wxr-plan-icons-not-included" />
                    Automatic rescraping
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faXmark} className="wxr-plan-icons-not-included" />
                    Remove Ultimo Bots Branding
                  </li>
                </ul>
                <button
                  className="wxr-wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("smart")}
                >
                  {t("start_free_trial")} {loading && <span className="wxr-spin" />}
                </button>
              </div>

              {/* BOOST */}
              <div
                className={`wxr-plan-card ${activePlanIdx === 1 ? "wxr-highlighted" : ""}`}
                ref={(el) => (planCardRefs.current[1] = el)}
              >
                <span className="wxr-plan-badge">{t("recommended_start")}</span>
                <h3 style={{ margin: '0' }}>{t("plan_boost")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="wxr-yearly-crossout">
                        <span className="wxr-wix-plan-price">
                          {t(priceMap.YEAR.boost.yearlyDiscountedKey)}
                        </span>
                        <span className="wxr-old-price">{t(priceMap.YEAR.boost.yearlyOriginalKey)}</span>
                        <span className="wxr-deal-tag">{t(priceMap.YEAR.boost.savePct)}</span>
                      </div>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wxr-wix-plan-price">{t(priceMap.MONTH.boost.labelKey)}</p>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="wxr-plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>2'000</strong> AI responses/month
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>1'000</strong> Knowledge Sources
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>10</strong> Custom Behaviors
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Chat
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Lead Capture
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Notifications
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Messaging
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-facebook">Facebook Messenger</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-instagram">Instagram DMs</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-whatsapp">WhatsApp</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <SlackLogo size={18} className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-slack">Slack</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <FontAwesomeIcon icon={faTelegram} className="wxr-feature-platform-icon" style={{ color: '#229ED9', fontSize: '18px' }} />
                      <span className="wxr-icon-tooltip wxr-telegram">Telegram</span>
                    </span>
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Sync data
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/notion-logo.png" alt="Notion" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-notion">Notion</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-google-drive">Google Drive</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-onedrive">Microsoft OneDrive</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Automatic rescraping (monthly)
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Remove Ultimo Bots Branding
                  </li>
                </ul>
                <button
                  className="wxr-wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("boost")}
                >
                  {t("start_free_trial")} {loading && <span className="wxr-spin" />}
                </button>
              </div>

              {/* ULTIMO */}
              <div
                className={`wxr-plan-card ${activePlanIdx === 2 ? "wxr-highlighted" : ""}`}
                ref={(el) => (planCardRefs.current[2] = el)}
              >
                <h3 style={{ margin: '0' }}>{t("plan_ultimo")}</h3>

                <PriceSwap showYearly={billingCycle === "YEAR"}
                  yearly={
                    <>
                      <div className="wxr-yearly-crossout">
                        <span className="wxr-wix-plan-price">
                          {t(priceMap.YEAR.ultimo.yearlyDiscountedKey)}
                        </span>
                        <span className="wxr-old-price">{t(priceMap.YEAR.ultimo.yearlyOriginalKey)}</span>
                        <span className="wxr-deal-tag">{t(priceMap.YEAR.ultimo.savePct)}</span>
                      </div>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                  monthly={
                    <>
                      <p className="wxr-wix-plan-price">{t(priceMap.MONTH.ultimo.labelKey)}</p>
                      <p className="wxr-wix-plan-month">{t("per_month")}</p>
                    </>
                  }
                />

                <ul className="wxr-plan-features">
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>100'000</strong> AI responses/month
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>10'000</strong> Knowledge Sources
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    <strong>50</strong> Custom Behaviors
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Chat
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Lead Capture
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Live Notifications
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Messaging
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/facebook-logo.webp" alt="Facebook" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-facebook">Facebook Messenger</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/instagram-logo.webp" alt="Instagram" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-instagram">Instagram DMs</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/whatsapp-logo.webp" alt="WhatsApp" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-whatsapp">WhatsApp</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <SlackLogo size={18} className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-slack">Slack</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <FontAwesomeIcon icon={faTelegram} className="wxr-feature-platform-icon" style={{ color: '#229ED9', fontSize: '18px' }} />
                      <span className="wxr-icon-tooltip wxr-telegram">Telegram</span>
                    </span>
                  </li>
                  <li className="wxr-plan-feature-icons">
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Sync data
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/notion-logo.png" alt="Notion" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-notion">Notion</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/google-drive.png" alt="Google Drive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-google-drive">Google Drive</span>
                    </span>
                    <span className="wxr-icon-tooltip-wrapper">
                      <img src="/onedrive.png" alt="OneDrive" className="wxr-feature-platform-icon" />
                      <span className="wxr-icon-tooltip wxr-onedrive">Microsoft OneDrive</span>
                    </span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Automatic rescraping (daily)
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheck} className="wxr-plan-icons-included" />
                    Remove Ultimo Bots Branding
                  </li>
                </ul>
                <button
                  className="wxr-wix-plan-button"
                  disabled={loading || isCheckingPlan}
                  onClick={() => handlePlanClick("ultimo")}
                >
                  {t("start_free_trial")} {loading && <span className="wxr-spin" />}
                </button>
              </div>
            </div>
          </div>

          <div className="wxr-pricing-indicators">
            {plans.map((_, i) => (
              <div
                key={i}
                className={`wxr-indicator ${i === activePlanIdx ? "wxr-active" : ""}`}
                onClick={() => setActivePlanIdx(i)}
              />
            ))}
          </div>

          <p className="wxr-cancel-note">{t("cancel_note")}</p>

          <p className="wxr-free-plan-link">
            <button
              type="button"
              className="wxr-free-plan-link-button"
              onClick={() => {
                window.open(
                  `https://manage.wix.com/dashboard/${siteId}/app/${process.env.REACT_APP_WIX_APP_ID}`,
                  "_self"
                );
              }}
            >
              Continue with free plan
            </button>
          </p>

        </>
      )}
    </div>,

    /* step 4/5 - success & next steps */
    <div className="wxr-card">
      <h2 className='wxr-complete-title'>Your chatbot is <span className='wxr-live-highlight'>live</span></h2>

      <div className="wxr-after-setup-links">
        <div className='wxr-integration-complete-content'>
          <FontAwesomeIcon icon={faPenToSquare} className='wxr-chatbot-integrated-icon'/>
          Modify & test your chatbot
        </div>
        <div className="wxr-centered-button-wrapper">
          <button
            className="wxr-integration-button-link"
            onClick={() => {
              window.open(`https://manage.wix.com/dashboard/${siteId}/app/${process.env.REACT_APP_WIX_APP_ID}`, '_self');
            }}
          >
            <div className='wxr-portal-button-content'>
              <FontAwesomeIcon icon={faCircleRight} className='wxr-forward-portal-icon'/>
              <>Ultimo Bots Portal</>
            </div>
          </button>
        </div>
      </div>
    </div>
  ];

  if (pageLoading) {
    return (
      <div className="wxr-loading-container">
        <div className="wxr-spinner"></div>
      </div>
    );
  }

  if (appNotInstalled) {
    return (
      <div className="wxr-wrapper wxr-register-scope">
        <img
          className="wxr-logo-register-site"
          src="/ultimo-bots-white.png"
          alt="logo"
        />

        <div className="wxr-card-button-wrapper">
          <div className="wxr-card">
            <h2>App not found</h2>
            <p className="wxr-sub">
              It looks like the Ultimo Bots app is not installed on your Wix site.
              <br />
              Please install or reinstall the app from the Wix App Market to get started.
            </p>
          </div>

          <button
            className="wxr-btn"
            onClick={() => (window.location.href = 'https://wix.to/89ZoJRg')}
          >
            Install Ultimo Bots
          </button>
        </div>
      </div>
    );
  }

  if (requiresAccessToken) {
    return (
      <div className="wxr-wrapper wxr-register-scope">
        <img
          className="wxr-logo-register-site"
          src="/ultimo-bots-white.png"
          alt="logo"
        />

        <div className="wxr-card-button-wrapper">
          <div className="wxr-card">
            <h2>Session expired </h2>
            <p className="wxr-sub">
              This session has expired for security reasons.
              <br />
              Click the button below to continue installing the app.
            </p>
          </div>

          <button
            className="wxr-btn"
            onClick={() => (window.location.href = 'https://wix.to/89ZoJRg')}
          >
            Manage app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wxr-wrapper wxr-register-scope">
      <img className="wxr-logo-register-site" src="/ultimo-bots-white.png" alt="logo" />
      <div className="wxr-step-ind">Step {step+1}/{slides.length}</div>
      <div className="wxr-steps-wrapper">
        <div className="wxr-steps-slider" style={{transform:`translateX(-${step*100}%)`}}>
          {slides.map((c,i)=>(
            <div key={i} className="wxr-step-slide">{c}</div>
          ))}
        </div>
      </div>

      <WarningModal
        open={showNoContentModal}
        onClose={() => setShowNoContentModal(false)}
        title="No Content Found"
        buttonText="Continue without knowledge"
        secondaryButtonText="Try a different URL"
        onSecondaryClick={handleTryAnotherUrl}
      >
        <p>
          We couldn't extract any content from your website. This can happen when the site
          isn't published yet or has limited publicly accessible content.
        </p>
        <p style={{ marginBottom: 0 }}>
          You can try a different URL, or continue and train your chatbot later in our portal
          by adding <strong>FAQ entries</strong>, uploading <strong>documents</strong>, or
          providing additional <strong>website links</strong>.
        </p>
      </WarningModal>
    </div>
  );
}
